import { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import apiClient from "../services/apiClient";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  UserOutlined,
  LockOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  MailOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import { ThemeContext } from "../context/ThemeContext";
import "./ModernLogin.css";
import TwoFactorModal from "./TwoFactorModal";
import { message } from "antd";
import { usePolicy } from "../context/PolicyContext";
import { useUser } from "../context/UserContext";
import { getPostLoginPath, isAdminRole } from "../utils/roleUtils";
import GoogleOAuthButton from "./GoogleOAuthButton";
import AppleOAuthButton from "./AppleOAuthButton";

const ModernLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.from;
  const { updatePolicies } = usePolicy();
  const { userData, forceRefreshUserData, applyAuthResponse } = useUser();

  const navigateAfterLogin = (roles, userEmail, extraState = {}) => {
    if (returnTo) {
      navigate(returnTo, { replace: true, state: extraState });
      return;
    }
    navigate(getPostLoginPath(roles), {
      state: { show2FAPopup: true, email: userEmail, ...extraState },
    });
  };

  const { isDark, toggleTheme } = useContext(ThemeContext);
  const [isLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [twoFAData, setTwoFAData] = useState({
    email: "",
    sessionId: "",
    availableMethods: [],
  });

  // Determine admin role using either fresh user data from the response
  // or the latest roles from UserContext as a fallback.
  const isUserAdmin = (sourceUserData) => {
    const roles =
      (sourceUserData && sourceUserData.roles) ||
      userData?.roles ||
      [];
    return isAdminRole(roles);
  };

  // Monitor loading state changes
  useEffect(() => {
    console.log("Loading state changed to:", isLoading);
  }, [isLoading]);

  const baseUrl = process.env.REACT_APP_AUTH_URL;
  const baseUrlRazorpay = process.env.REACT_APP_RAZORPAY_BASE_URL;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // const loginRazorPay = async () => {
  //   let formData = { email: "admin@svirtz.com", password: "admin@" };
  //   try {
  //     const response = await apiClient.post(
  //       `${baseUrlRazorpay}/api/auth/signin`,
  //       formData
  //     );
  //     console.log("Razorpay Response:", response.data);
  //     localStorage.setItem("razorpayAuthToken", response.data.accessToken);
  //   } catch (error) {
  //     console.error("Razorpay Error:", error);
  //   }
  // };
  //  setTimeout(() => {
  //             navigate("/d/dash", {
  //               state: {
  //                 show2FAPopup: true,
  //                 email: response.data.email,
  //               },
  //             });
  //           }, 1000);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Handle login
    try {
      const loginData = {
        email: formData.email,
        password: formData.password,
      };

      console.log("Attempting login with:", loginData);
      console.log("Using base URL:", baseUrl);
      const response = await apiClient.post(`${baseUrl}/user/login`, loginData);
      console.log("Login Response:", response.data);
      if (!response.data.success) {
        throw new Error("Login failed - success field is false");
      }
      // Backend now sets HttpOnly cookies; we only read user data
      const userData = response.data.data;
      updatePolicies(userData.policies);

      // Force refresh user data to ensure latest user info is loaded
      forceRefreshUserData();

      if (userData.lastPasswordChange === null) {
        message.info("User Need to Change Password First")
        console.log(
          "User needs to change password, redirecting to change password page..."
        );
        setIsLoading(false);
        navigate("/change-password", {
          state: {
            email: userData.email,
            currentPassword: formData.password,
            roles: userData.roles,
          }
        });
        return;
      }

      console.log("User data:", userData);
      if (
        userData &&
        userData.availableMethods &&
        userData.availableMethods.length > 0 &&
        userData.sessionId
      ) {
        // toast.error(userData.message, {
        //   position: "top-right",
        //   autoClose: 3000,
        //   hideProgressBar: false,
        //   closeOnClick: true,
        //   pauseOnHover: true,
        //   draggable: true,
        //   progress: undefined,
        //   theme: isDark ? "dark" : "light",
        // });
        setShow2FAModal(true);
        setTwoFAData({
          email: loginData.email,
          sessionId: userData.sessionId,
          availableMethods: userData.availableMethods || [],
        });
        setIsLoading(false);
        return;
      }

      message.success("Login successful!");

      // Navigate based on user role
      setTimeout(() => {
        console.log("ModernLogin - Login success - Updating policies", userData.policies);
        const admin = isUserAdmin(userData);
        console.log("ModernLogin - Login success - User is admin:", admin);
        navigateAfterLogin(userData.roles || [], userData.email);
      }, 500);

    } catch (error) {
      // Handle account not verified (406) error
      if (
        error.response &&
        error.response.status === 406 &&
        error.response.data &&
        error.response.data.success === true &&
        error.response.data.data
      ) {
        console.log("Account not verified, redirecting to OTP verification");
        setIsLoading(false);
        
        // Show info message
        message.info(error.response.data.data.message || "Please verify your email to continue");
        
        // Redirect to OTP verification page
        navigate("/verify-otp", {
          state: {
            email: formData.email,
            message: error.response.data.data.message
          }
        });
        return;
      }
      
      // Handle 2FA-required 401 error
      if (
        error.response &&
        error.response.status === 401 &&
        error.response.data &&
        error.response.data.error === "2FA_REQUIRED" &&
        error.response.data.sessionId
      ) {
        setShow2FAModal(true);
        setTwoFAData({
          email: formData.email,
          sessionId: error.response.data.sessionId,
          availableMethods: error.response.data.availableMethods || [],
        });
        setIsLoading(false);
        return;
      }

      // Handle invalid credentials 401 error
      if (
        error.response &&
        error.response.status === 401 &&
        error.response.data &&
        error.response.data.error === "Invalid username or password"
      ) {
        console.error("Invalid credentials error:", error.response.data);
        setIsLoading(false);
        message.error("Invalid username or password");
        return;
      }

      console.error("Login Error:", error);
      setIsLoading(false);

      let errorMessage = "Invalid credentials. Please try again.";

      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log("Error response data:", error.response.data);
        console.log("Error response status:", error.response.status);
        console.log("Error response headers:", error.response.headers);
        
        // Handle different error response structures
        if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        // The request was made but no response was received
        console.log("Error request:", error.request);
        errorMessage = "Network error. Please check your connection.";
      } else {
        // Something happened in setting up the request that triggered an Error
        console.log("Error message:", error.message);
        errorMessage = "An error occurred. Please try again.";
      }

      message.error(errorMessage);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleOAuthSuccess = (oauthUserData) => {
    updatePolicies(oauthUserData.policies);
    applyAuthResponse(oauthUserData);
    forceRefreshUserData();
    
    setTimeout(() => {
      const admin = isUserAdmin(oauthUserData);
      navigateAfterLogin(oauthUserData.roles || [], oauthUserData.email);
    }, 500);
  };

  const handleOAuthError = (error) => {
    console.error('OAuth error:', error);
  };



  return (
    <div className={`modern-login-container ${isDark ? "dark" : ""}`}>
      <div className="login-content">
        {/* Centered Form Section */}
        <div className="login-form-section">
          <div className="login-card">
            {/* Theme Toggle */}
            <button
              className="theme-toggle-btn"
              onClick={toggleTheme}
              title={`Switch to ${isDark ? "light" : "dark"} mode`}
            >
              {isDark ? "☀️" : "🌙"}
            </button>

            {/* Header */}
            <div className="login-header">
              <h2 className="welcome-title">Welcome to <strong>Simply FI</strong></h2>
              <p className="login-subtitle">
                Your gateway to intelligent bond investments. Discover, analyze, and invest in bonds with confidence using our advanced platform.
              </p>
            </div>

            {/* Form */}
            <form className="login-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <div className="input-container">
                  <div className="input-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <polyline points="22,6 12,13 2,6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="user@simplyfi.com"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <div className="input-container password-input-container">
                  <div className="input-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="#9CA3AF" strokeWidth="2" />
                      <circle cx="12" cy="16" r="1" fill="#9CA3AF" />
                      <path d="M7 11V7A5 5 0 0 1 17 7V11" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="form-input password-input"
                    placeholder="Password"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M1 1l22 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div style={{ textAlign: "right", marginBottom: "0.5rem" }}>
                <button
                  type="button"
                  className="forgot-password-btn"
                  onClick={() => navigate("/forgot-password")}
                >
                  Forgot password?
                </button>
              </div>

              <button type="submit" className="submit-btn" disabled={isLoading}>
                {isLoading ? "Processing..." : "Login"}
              </button>
            </form>

            {/* Divider */}
            <div className="divider">
              <span>or</span>
            </div>

            {/* Social Login */}
            <div className="social-login">
              <GoogleOAuthButton 
                onSuccess={handleOAuthSuccess}
                onError={handleOAuthError}
                buttonText="Continue with Google"
              />
              {/* Apple Sign In disabled - requires $99/year Apple Developer Program
              <AppleOAuthButton 
                onSuccess={handleOAuthSuccess}
                onError={handleOAuthError}
                buttonText="Continue with Apple"
              />
              */}
            </div>

            {/* Toggle Form */}
            <div className="toggle-form">
              <span className="toggle-text">
                Don't have an account? <strong onClick={() => navigate("/signup-modern")} style={{ cursor: 'pointer' }}>Sign Up</strong>
              </span>
            </div>
          </div>
        </div>
      </div>
      <TwoFactorModal
        visible={show2FAModal}
        onCancel={() => setShow2FAModal(false)}
        email={twoFAData.email}
        sessionId={twoFAData.sessionId}
        availableMethods={twoFAData.availableMethods}
        onSuccess={(data) => {
          setShow2FAModal(false);
          setTwoFAData({ email: "", sessionId: "", availableMethods: [] });

          // Force refresh user data after 2FA success
          forceRefreshUserData();

          // Navigate based on user role
          const admin = isUserAdmin(data);
          console.log("ModernLogin - TwoFactorModal - User is admin:", admin);
          navigateAfterLogin(data?.roles || userData?.roles || [], data?.email || twoFAData.email);
        }}
      />
    </div>
  );
};

export default ModernLogin;
